(function (window, document) {
  'use strict';

  var app = window.MNBListing;
  if (!app || typeof app.register !== 'function') return;

  var context = null;
  var pending = [];
  var googleLoading = null;
  var fingerprintLoading = null;

  function isDebugMode() {
    try {
      return window.sessionStorage && window.sessionStorage.getItem('debug_mode') === 'true';
    } catch (_error) {
      return false;
    }
  }

  function hasKey(value) {
    return String(value || '').trim().length > 0;
  }

  function normalizeEmail(value) {
    return String(value || '').trim().toLowerCase().replace(/\s/g, '');
  }

  function normalizePhone(value) {
    var phone = String(value || '').replace(/[\s\-().]/g, '');
    if (phone.indexOf('0044') === 0) phone = '+44' + phone.slice(4);
    return phone;
  }

  function safePostHogCall(method, args) {
    var client = window.posthog;
    try {
      if (client && typeof client[method] === 'function') {
        client[method].apply(client, args);
        return true;
      }
    } catch (_error) {}
    return false;
  }

  function flush() {
    if (isDebugMode() || !pending.length) {
      if (isDebugMode()) pending = [];
      return;
    }

    pending = pending.filter(function (item) {
      return !safePostHogCall(item.method, item.args);
    });
  }

  function queueOrRun(method, args) {
    if (isDebugMode()) return;
    flush();
    if (!safePostHogCall(method, args)) pending.push({ method: method, args: args });
  }

  function capture(event, properties) {
    queueOrRun('capture', [event, properties || {}]);
  }

  function identify(email) {
    var normalized = normalizeEmail(email);
    var currentId;
    if (!normalized || normalized.indexOf('@') === -1) return;
    if (isDebugMode()) return;
    try {
      currentId = window.posthog && typeof window.posthog.get_distinct_id === 'function'
        ? window.posthog.get_distinct_id()
        : null;
    } catch (_error) {
      currentId = null;
    }
    if (currentId === normalized) return;
    queueOrRun('identify', [normalized, { email: normalized }]);
  }

  function setPersonProperties(properties) {
    if (isDebugMode()) return;
    queueOrRun('setPersonProperties', [properties || {}]);
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.defer = true;
      script.addEventListener('load', function () { resolve(); });
      script.addEventListener('error', function () { reject(new Error('Failed to load ' + src)); });
      document.head.appendChild(script);
    });
  }

  function ensureFingerprint() {
    var key = context && context.config && context.config.fingerprintApiKey;
    if (!hasKey(key) || isDebugMode()) return Promise.resolve(null);
    if (window.Fingerprint && typeof window.Fingerprint.start === 'function') return Promise.resolve(window.Fingerprint);
    if (!fingerprintLoading) {
      fingerprintLoading = loadScript('https://fpjscdn.net/v4/' + encodeURIComponent(String(key).trim()) + '/iife.min.js')
        .then(function () { return window.Fingerprint || null; })
        .catch(function () { return null; });
    }
    return fingerprintLoading;
  }

  function fingerprint() {
    var key = context && context.config && context.config.fingerprintApiKey;
    if (!hasKey(key) || isDebugMode()) return Promise.resolve(null);
    return ensureFingerprint().then(function (sdk) {
      if (!sdk || typeof sdk.start !== 'function') return null;
      return sdk.start({ apiKey: String(key).trim(), region: 'eu' });
    }).then(function (agent) {
      if (!agent || typeof agent.get !== 'function') return null;
      return agent.get();
    }).then(function (result) {
      var visitorId = result && (result.visitorId || result.visitor_id);
      if (visitorId) setPersonProperties({ fingerprint_visitor_id: visitorId });
      return result || null;
    }).catch(function (error) {
      if (window.console && typeof window.console.warn === 'function') {
        window.console.warn('Fingerprint unavailable:', error && error.message ? error.message : String(error));
      }
      return null;
    });
  }

  // AddressCapture.tsx:55-65 only reveals the manual fields once the seller has
  // an address (from Places) or has clicked the button themselves, and hides
  // the button once the fields are showing. Mirror that: never reveal on
  // mount just because Places didn't load, so a missing/broken API key can't
  // pop fields the seller never asked for.
  function revealManualAddressFields(input) {
    var root = input && input.closest ? input.closest('[data-new-listing-root]') : context && context.root;
    Array.prototype.slice.call((root || document).querySelectorAll('[data-address-manual-fields]')).forEach(function (node) {
      node.hidden = false;
    });
    Array.prototype.slice.call((root || document).querySelectorAll('[data-address-manual-toggle]')).forEach(function (node) {
      node.hidden = true;
    });
  }

  // A rejected Places key (RefererNotAllowedMapError and friends) leaves the
  // search input dead — the gm-err-autocomplete error state with the warning
  // icon and a disabled field. Hide the broken field and hand the seller the
  // manual fields straight away instead.
  function fallbackToManualAddress(input) {
    var root = input && input.closest ? input.closest('[data-new-listing-root]') : context && context.root;
    Array.prototype.slice.call((root || document).querySelectorAll('[data-address-search]')).forEach(function (node) {
      (node.closest('.nl-field') || node).hidden = true;
    });
    revealManualAddressFields(root);
  }

  function watchForAutocompleteError(input) {
    if (typeof window.MutationObserver !== 'function') return;
    var observer = new window.MutationObserver(function () {
      if (input.classList.contains('gm-err-autocomplete')) {
        observer.disconnect();
        fallbackToManualAddress(input);
      }
    });
    observer.observe(input, { attributes: true, attributeFilter: ['class', 'disabled'] });
  }

  var authFallbackInstalled = false;

  // Google invokes the global gm_authFailure for every authentication error
  // (bad key, referer not allowed) as soon as it detects one, so this covers
  // failures that happen before the seller even focuses the field.
  function installAuthFailureFallback() {
    if (authFallbackInstalled) return;
    authFallbackInstalled = true;
    window.gm_authFailure = function () {
      fallbackToManualAddress(null);
    };
  }

  function applyAddressComponents(components) {
    var values = {};
    (components || []).forEach(function (component) {
      var types = component.types || [];
      if (types.indexOf('country') !== -1) {
        values.address_country = component.long_name || '';
        values.address_countryCode = component.short_name || component.long_name || '';
      } else if (types.indexOf('street_number') !== -1) {
        values.address_house_number = component.long_name || '';
      } else if (types.indexOf('route') !== -1) {
        values.address_street_name = component.long_name || '';
      } else if (types.indexOf('postal_town') !== -1 || types.indexOf('locality') !== -1) {
        values.address_city = component.long_name || '';
      } else if (types.indexOf('postal_code') !== -1) {
        values.address_postcode = component.long_name || '';
      } else if (types.indexOf('administrative_area_level_2') !== -1) {
        values.address_state = component.long_name || '';
      }
    });
    if (context && context.store && typeof context.store.patch === 'function') context.store.patch(values);
    return values;
  }

  function ensureGooglePlaces() {
    var key = context && context.config && context.config.googleApiKey;
    if (!hasKey(key) || isDebugMode()) return Promise.resolve(null);
    if (window.google && window.google.maps && window.google.maps.places) return Promise.resolve(window.google.maps.places);
    if (!googleLoading) {
      googleLoading = loadScript('https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(String(key).trim()) + '&libraries=places')
        .then(function () {
          return window.google && window.google.maps && window.google.maps.places ? window.google.maps.places : null;
        })
        .catch(function () { return null; });
    }
    return googleLoading;
  }

  function mountAddress(input) {
    var key = context && context.config && context.config.googleApiKey;
    var autocomplete;

    if (!input) return null;

    // Debug mode intentionally skips Places so QA can reach every field without
    // a live key; a seller with no key configured still gets the ordinary
    // "Manually enter address" button instead of fields appearing unasked.
    if (isDebugMode()) {
      revealManualAddressFields(input);
      return null;
    }
    if (!hasKey(key)) return null;

    if (window.google && window.google.maps && window.google.maps.places && window.google.maps.places.Autocomplete) {
      autocomplete = new window.google.maps.places.Autocomplete(input, {
        types: ['address'],
        componentRestrictions: { country: 'GB' },
        fields: ['address_components']
      });
      autocomplete.addListener('place_changed', function () {
        var place = autocomplete.getPlace();
        applyAddressComponents(place && place.address_components);
        revealManualAddressFields(input);
      });
      watchForAutocompleteError(input);
      return autocomplete;
    }

    ensureGooglePlaces().then(function (places) {
      if (!places || !places.Autocomplete) return null;
      return mountAddress(input);
    }).catch(function () {});
    return null;
  }

  function bindContactControls() {
    var root = context && context.root;
    var state = context && context.store && typeof context.store.get === 'function' ? context.store.get() : {};
    if (!root) return;
    // AddressCapture.tsx:18 — hasAddress reflects a restored draft too, not
    // only a fresh Places selection or a manual click.
    if (state && state.address_street_name) revealManualAddressFields(root);
    Array.prototype.slice.call(root.querySelectorAll('[data-address-search]')).forEach(function (input) {
      mountAddress(input);
    });
    root.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-address-manual-toggle]');
      if (!trigger || !root.contains(trigger)) return;
      event.preventDefault();
      revealManualAddressFields(trigger);
    });
    root.addEventListener('blur', function (event) {
      var input = event.target && event.target.closest ? event.target.closest('[data-field="user_Email"]') : null;
      if (!input || !root.contains(input)) return;
      identify(input.value);
    }, true);
  }

  function init(nextContext) {
    context = nextContext || {};
    installAuthFailureFallback();
    bindContactControls();
    if (isDebugMode()) {
      pending = [];
      return;
    }
    fingerprint();
    flush();
  }

  app.register('integrations', {
    init: init,
    capture: capture,
    identify: identify,
    fingerprint: fingerprint,
    mountAddress: mountAddress,
    normalizePhone: normalizePhone,
    destroy: function () {
      pending = [];
      context = null;
    }
  });
})(window, document);
